import React, { useEffect, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

/**
 * AnalyticsTracker component
 * 
 * This component tracks page views and user interactions.
 * It should be included in the layout of public-facing pages.
 * 
 * @param {Object} props
 * @param {string} props.pageId - The ID of the current page
 * @param {string} props.microSiteId - The ID of the current microsite
 * @param {string} props.accountId - The ID of the account (optional - will be retrieved from microsite if missing)
 * @param {boolean} props.trackInteractions - Whether to track user interactions (default: true)
 */
const AnalyticsTracker = ({ pageId, microSiteId, accountId, trackInteractions = true }) => {
  const [sessionId, setSessionId] = useState(null);
  const [previousPageId, setPreviousPageId] = useState(null);
  const [trackingEnabled, setTrackingEnabled] = useState(true);

  useEffect(() => {
    // Validate required props - only pageId and microSiteId are strictly required
    if (!pageId || !microSiteId) {
      console.error('AnalyticsTracker: Missing required props', { pageId, microSiteId });
      setTrackingEnabled(false);
      return;
    }

    // Log a warning if accountId is missing but continue with tracking
    if (!accountId) {
      console.warn('AnalyticsTracker: accountId is missing, server will attempt to retrieve it from microSiteId');
    }
    
    // Initialize or get session ID
    let sid = localStorage.getItem('analytics_session_id');
    const sessionTimestamp = localStorage.getItem('analytics_session_timestamp');
    
    // Check if session has expired (30 minutes)
    const now = Date.now();
    if (!sid || !sessionTimestamp || now - parseInt(sessionTimestamp, 10) > 30 * 60 * 1000) {
      // Create new session
      sid = uuidv4();
      localStorage.setItem('analytics_session_id', sid);
      localStorage.setItem('analytics_session_timestamp', now.toString());
    } else {
      // Update session timestamp
      localStorage.setItem('analytics_session_timestamp', now.toString());
    }
    
    setSessionId(sid);
    
    // Get previous page ID if available
    const prevPageId = localStorage.getItem('analytics_previous_page_id');
    setPreviousPageId(prevPageId);
    
    // Store current page ID for next page view
    localStorage.setItem('analytics_previous_page_id', pageId);
    
    // Track page view
    trackPageView(sid, prevPageId);
    
    // Set up interaction tracking
    if (trackInteractions) {
      setupInteractionTracking(sid);
    }
    
    // Cleanup
    return () => {
      if (trackInteractions) {
        cleanupInteractionTracking();
      }
    };
  }, [pageId, microSiteId, accountId, trackInteractions]);

  /**
   * Get the base URL for API calls
   * This handles both development and production environments
   */
  const getApiBaseUrl = () => {
    // Always use the current origin for API calls to avoid CORS issues
    return window.location.origin;
  };

  /**
   * Track a page view
   */
  const trackPageView = async (sid, prevPageId) => {
    if (!trackingEnabled) return;
    
    try {
      const apiUrl = `${getApiBaseUrl()}/api/analytics/track/pageview`;
      
      // Create the request payload, including accountId only if it exists
      const payload = {
        pageId,
        microSiteId,
        sessionId: sid,
        referrer: document.referrer || null,
        previousPageId: prevPageId || null
      };
      
      // Only include accountId if it exists
      if (accountId) {
        payload.accountId = accountId;
      }
      
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(payload)
      });
      
      if (!response.ok) {
        console.error('Failed to track page view:', response.status, response.statusText);
      }
    } catch (error) {
      console.error('Error tracking page view:', error);
    }
  };

  /**
   * Track a user interaction
   */
  const trackInteraction = async (eventType, elementId, elementType, data = {}) => {
    if (!trackingEnabled || !sessionId) return;
    
    try {
      const apiUrl = `${getApiBaseUrl()}/api/analytics/track/interaction`;
      
      // Create the request payload, including accountId only if it exists
      const payload = {
        pageId,
        microSiteId,
        sessionId,
        eventType,
        elementId,
        elementType,
        data
      };
      
      // Only include accountId if it exists
      if (accountId) {
        payload.accountId = accountId;
      }
      
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(payload)
      });
      
      if (!response.ok) {
        console.error('Failed to track interaction:', response.status, response.statusText);
      }
    } catch (error) {
      console.error('Error tracking interaction:', error);
    }
  };

  /**
   * Set up event listeners for tracking interactions
   */
  const setupInteractionTracking = (sid) => {
    // Track clicks
    document.addEventListener('click', handleClick);
    
    // Track form submissions
    document.addEventListener('submit', handleFormSubmit);
    
    // Track scroll depth
    window.addEventListener('scroll', handleScroll);
    
    // Track page exit
    window.addEventListener('beforeunload', handleBeforeUnload);
  };

  /**
   * Clean up event listeners
   */
  const cleanupInteractionTracking = () => {
    document.removeEventListener('click', handleClick);
    document.removeEventListener('submit', handleFormSubmit);
    window.removeEventListener('scroll', handleScroll);
    window.removeEventListener('beforeunload', handleBeforeUnload);
  };

  /**
   * Handle click events
   */
  const handleClick = (event) => {
    // Find the closest element with an ID or data-analytics-id
    let target = event.target;
    let elementId = null;
    let elementType = null;
    
    // Traverse up the DOM tree to find an identifiable element
    while (target && target !== document) {
      elementId = target.id || target.getAttribute('data-analytics-id');
      
      if (elementId) {
        // Determine element type
        if (target.tagName === 'A') {
          elementType = 'link';
        } else if (target.tagName === 'BUTTON') {
          elementType = 'button';
        } else if (target.tagName === 'INPUT' && target.type === 'button') {
          elementType = 'button';
        } else {
          elementType = target.tagName.toLowerCase();
        }
        
        // Track the click
        trackInteraction('click', elementId, elementType, {
          text: target.innerText || target.textContent,
          href: target.href || null,
          x: event.clientX,
          y: event.clientY
        });
        
        break;
      }
      
      target = target.parentElement;
    }
    
    // If no element with ID was found, track with a generated ID
    if (!elementId) {
      const generatedId = `element-${Math.floor(Math.random() * 1000000)}`;
      elementType = target.tagName.toLowerCase();
      
      trackInteraction('click', generatedId, elementType, {
        text: target.innerText || target.textContent,
        href: target.href || null,
        x: event.clientX,
        y: event.clientY
      });
    }
  };

  /**
   * Handle form submission events
   */
  const handleFormSubmit = (event) => {
    const form = event.target;
    const formId = form.id || form.getAttribute('data-analytics-id') || `form-${Math.floor(Math.random() * 1000000)}`;
    
    trackInteraction('form_submit', formId, 'form', {
      action: form.action || window.location.href,
      method: form.method || 'GET'
    });
  };

  /**
   * Handle scroll events
   */
  let lastScrollDepth = 0;
  const handleScroll = () => {
    // Throttle scroll events
    if (!window.requestAnimationFrame) {
      return;
    }
    
    window.requestAnimationFrame(() => {
      // Calculate scroll depth as percentage
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const scrollHeight = document.documentElement.scrollHeight - document.documentElement.clientHeight;
      const scrollDepth = Math.round((scrollTop / scrollHeight) * 100);
      
      // Only track when crossing 25%, 50%, 75%, and 100% thresholds
      const thresholds = [25, 50, 75, 100];
      
      for (const threshold of thresholds) {
        if (lastScrollDepth < threshold && scrollDepth >= threshold) {
          trackInteraction('scroll_depth', `scroll-${threshold}`, 'scroll', {
            depth: threshold
          });
          break;
        }
      }
      
      lastScrollDepth = scrollDepth;
    });
  };

  /**
   * Handle page exit events
   */
  const handleBeforeUnload = () => {
    // Track page exit
    trackInteraction('page_exit', 'window', 'window', {
      timeOnPage: Math.floor((Date.now() - parseInt(localStorage.getItem('analytics_session_timestamp'), 10)) / 1000)
    });
  };

  // This component doesn't render anything
  return null;
};

export default AnalyticsTracker; 